import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import Button, { ButtonType } from 'inquiry/components/Button';
import ConfirmFileUploadView from 'inquiry/components/ConfirmFileUploadView';
import ImageOriented from 'inquiry/components/ImageOriented';
import InlineLoadingIndicator from 'inquiry/components/InlineLoadingIndicator';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useStepDocumentStyleContext } from 'inquiry/context/StepDocumentStyleContext';
import { StepDocumentConfig } from 'lib/interfaces/StepsConfig';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer';
import { StoredFileUpload } from 'verify/lib/interfaces/fileUploads';
import { CaptureMethod } from 'verify/models/verification';
import { IMAGE_TYPES } from 'verify/redux/thunk-actions/document';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

const ImagePositioner = styled('div')`
  img {
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    max-height: 320px;

    margin-bottom: ${SPACINGS.base * 2}px;
  }
`;

const Minify = styled('div')`
  display: inline;

  img {
    height: 12px;
    margin-bottom: 4px;
  }
`;

interface InquiryDocumentsCheckViewProps {
  currentUpload: StoredFileUpload;
  currentUploadCaptureMethod: CaptureMethod;
  isProcessing: boolean;
  localizations: StepDocumentConfig['localizations']['check-page'];
  handleContinue: () => Promise<void>;
  handleRetry: () => void;
}

export default function InquiryDocumentsCheckView(props: InquiryDocumentsCheckViewProps) {
  const { base, buttons: buttonStyles } = useStepDocumentStyleContext();
  const {
    currentUpload,
    currentUploadCaptureMethod,
    localizations,
    handleContinue,
    handleRetry,
    isProcessing,
  } = props;

  if (
    !IMAGE_TYPES.includes(currentUpload.fileType) ||
    currentUploadCaptureMethod === CaptureMethod.Upload
  ) {
    return (
      <ConfirmFileUploadView
        fileName={currentUpload.filename}
        isProcessing={isProcessing}
        handleContinue={handleContinue}
        handleRetry={handleRetry}
        title={localizations['confirm-upload-title']}
        prompt={localizations['confirm-upload-prompt']}
        uploading={localizations['confirm-upload-uploading']}
        btnSubmit={localizations['confirm-upload-btn-submit']}
        btnRetake={localizations['confirm-upload-btn-retake']}
        titleStyles={base?.titleStyle}
        textStyles={base?.textStyle}
        buttonStyles={buttonStyles}
      />
    );
  }

  return (
    <Container>
      <Header>
        <ThemedTitle styles={base?.titleStyle}>
          <Markdown>{localizations.title}</Markdown>
        </ThemedTitle>
        <ThemedParagraph styles={base?.textStyle}>
          <Markdown>{localizations.prompt}</Markdown>
        </ThemedParagraph>
      </Header>

      <ImagePositioner>
        <ImageOriented src={currentUpload.dataUrl} alt='upload preview' />
      </ImagePositioner>

      <PositionableButtonContainer>
        <Button
          id='documents-check__use-image'
          buttonType={ButtonType.Primary}
          styles={buttonStyles?.[ButtonType.Primary]}
          disabled={isProcessing}
          onClick={handleContinue}
          data-test='documents-check__continue'
        >
          {isProcessing ? (
            <span>
              {localizations.uploading}
              <Minify>
                <InlineLoadingIndicator />
              </Minify>
            </span>
          ) : (
            localizations['btn-submit']
          )}
        </Button>

        {/* Assume spacing is specified by styles if present */}
        {buttonStyles == null && <Spacer height={2} />}

        <Button
          id='documents-check__retake'
          buttonType={ButtonType.Secondary}
          styles={buttonStyles?.[ButtonType.Secondary]}
          disabled={isProcessing}
          onClick={handleRetry}
          data-test='documents-check__retake'
        >
          {localizations['btn-retake']}
        </Button>
      </PositionableButtonContainer>
    </Container>
  );
}
